import * as jobApi from '@/api/job';
import { jobStateEnum } from '@/api/job';
import * as jupyterApi from '@/api/jupyter';
import ActionButtonGroup from '@/components/actionButtonGroup';
import { fileEventQueue } from '@/utils/fileEvent';
import { fileService } from '@/utils/filesService';
import { FullscreenOutlined, StopOutlined } from '@ant-design/icons';
import { App, Button, Checkbox, Form, FormProps, InputNumber, message, Select, Tag, Typography } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const parseWalltimeToMinutes = (walltime?: string): number => {
    if (!walltime) return 1440; // default value (24 hours in minutes)
    const parts = walltime.split(':');
    if (parts.length !== 3) return 1440;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2], 10);
    return hours * 60 + minutes + seconds / 60;
};

const { Title, Paragraph } = Typography;
const JupyterPage: React.FC = () => {
    const { modal } = App.useApp();

    const [jupyterUrl, setJupyterUrl] = useState<string | undefined>();
    const [jupyterJobId, setjupyterJobId] = useState<number | undefined>();
    const [jupyterState, setjupyterState] = useState<jobStateEnum | undefined>()
    const [jupyterInstanceInfo, setJupyterInstanceInfo] = useState<any>(undefined);
    const [openInNewTab, setOpenInNewTab] = useState<boolean>(true);
    const [showJupyterIframe, setShowJupyterIframe] = useState<boolean>(false);
    const { t } = useTranslation('common');

    const machineMapRef = useRef<{ [partition: string]: string[] }>();
    const [partitionOptions, setPartitionOptions] = useState<{ label: string, value: string }[]>([]);
    const [nodesOptions, setNodesOptions] = useState<{ label: string, value: string }[]>([]);

    const [form] = Form.useForm<{
        cpu: number,
        gpu: number,
        partition?: string,
        node?: string,
        maxTime?: number
    }>();

    const { run: queryUserResource, data: userResource } = useRequest(jobApi.queryUserResource, {
        manual: true,
        onSuccess: (res) => {
            if (res?.ok) {
                console.log("User resource data:", userResource);
            }
        }
    })

    const onFormChange: FormProps['onFieldsChange'] = (changedFields) => {
        if (changedFields[0].name[0] === 'partition') {
            updateNodeOptions(changedFields[0].value)
            const { node } = form.getFieldsValue();
            queryUserResource({ partition: changedFields[0].value, node })
            if (changedFields[0].value === "gpu") {
                form.setFieldValue('gpu', 1)
            } else {
                form.setFieldValue('gpu', 0)
            }
        } else if (changedFields[0].name[0] === 'node') {
            const { partition } = form.getFieldsValue();
            queryUserResource({ partition, node: changedFields[0].value })
        }
    }

    useEffect(() => {
        fileEventQueue.setEventCallback(`jupyter`, async (events) => {
            events.forEach(event => {
                if (event.path.includes(".jupyter/parameters.json")) {
                    getCurrentJupyterJob()
                }
            })
        })

        return () => fileService.unlistenToJupyterConfig();
    }, []);

    const updateNodeOptions = (partition: string) => {
        if (!machineMapRef.current) {
            return;
        }

        if (partition === 'default') {
            setNodesOptions([]);
            form.setFieldsValue({ 'node': 'default' });
            return;
        }

        const nodes = machineMapRef.current[partition].map((node) => ({
            label: node,
            value: node
        }));
        nodes.unshift({ label: t('jupyter.default'), value: 'default' });
        console.log("nodes", nodes)
        setNodesOptions(nodes);
        form.setFieldsValue({ 'node': 'default' });
    }

    const storeAuthToken = () => {
        const token = localStorage.getItem('token');
        if (token) {
            sessionStorage.setItem('jupyterAuth', token);
        }
    }

    const getCurrentJupyterJob = async () => {
        const jobRes = await jupyterApi.getJupyterJob()
        if (jobRes?.ok && jobRes?.data) {
            if (jobRes?.data) {
                if (jobRes?.data?.state == jobStateEnum.PENDING) {
                    setjupyterState(jobStateEnum.PENDING)
                } else if (jobRes?.data?.state == jobStateEnum.RUNNING) {
                    const jobId = jobRes?.data?.idJob;
                    const res = await jupyterApi.getJupyterJobById(jobRes?.data?.idJob);
                    // if pending, then set state to pending
                    if (res?.ok && res.data && res.data?.url) {
                        setjupyterJobId(jobId);
                        setJupyterInstanceInfo(res?.data);
                        await onOpenJupyter(res.data.url);
                        setjupyterState(jobStateEnum.RUNNING)
                    } else {
                        setjupyterState(jobStateEnum.PENDING)
                    }
                }
            } else {
                setjupyterState(jobStateEnum.PENDING)
            }
            return jobRes.data;
        }
        return undefined;
    }

    const fetchMachineInfo = async () => {
        const res = await jobApi.getMachineList();
        if (res?.ok) {
            const data = res?.data || [];
            machineMapRef.current = res.data;
            const PartitionOptions = Object.keys(data).map((partition) => ({
                label: partition,
                value: partition
            }))
            PartitionOptions.unshift({ label: t('jupyter.default'), value: 'default' })
            form.setFieldValue('partition', 'default')
            setPartitionOptions(PartitionOptions)
            machineMapRef.current = data
        } else {
            message.error(res?.message || t('jupyter.machine_failed'));
        }
    }

    useEffect(() => {
        getCurrentJupyterJob()
        fetchMachineInfo()
    }, []);

    const onOpenJupyter = async (url: string) => {
        storeAuthToken(); // Store auth token before opening
        console.log("url VITE_API_URL:", import.meta.env.VITE_API_URL)
        if (import.meta.env.VITE_API_URL) {
            console.log(import.meta.env.VITE_API_URL + url)
            setJupyterUrl(import.meta.env.VITE_API_URL + url)
        } else {
            console.log("url:", window.origin + url)
            setJupyterUrl(window.origin + url)
        }
    }

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const onLaunchJupyter = async () => {
        setIsLoading(true);
        const { cpu, gpu, partition, node, maxTime } = form.getFieldsValue();

        const param = {
            cpu: cpu ?? 1,
            gpu: gpu ?? 0,
            partition: partition === 'default' ? undefined : partition,
            node: node === 'default' ? undefined : node,
            maxTime: maxTime
        }

        const res = await jupyterApi.launchJupyterJob(param);
        if (res?.ok) {
            message.info(t('jupyter.submit_success'));
            fileService.listenToJupyterConfig()
            setjupyterState(jobStateEnum.PENDING)
            // let attempts = 0;
            // const maxAttempts = 5;
            // const interval = setInterval(async () => {
            //     if (attempts >= maxAttempts) {
            //         message.error('Failed to launch Jupyter job. Please try again later.');
            //         clearInterval(interval);
            //         return;
            //     }
            //     const jobInfo = await getCurrentJupyterJob();
            //     if (jobInfo) {
            //         clearInterval(interval);
            //     } else {
            //         attempts++;
            //     }
            // }, 1000);
        } else {
            message.error(res?.message ?? 'Failed to launch Jupyter');
        }
        setIsLoading(false);
    }

    const fullScreenHandler = () => {
        if (jupyterUrl) {
            window.open(jupyterUrl, '_blank');
        }
    }

    const handleOpenJupyter = () => {
        if (openInNewTab) {
            fullScreenHandler();
        } else {
            setShowJupyterIframe(true);
        }
    }

    const cancelJobHandler = async () => {
        if (jupyterJobId === undefined) {
            return;
        }
        const res = await jobApi.cancelJob(jupyterJobId);
        if (res?.ok) {
            message.success(t('jupyter.cancel_success'));
            fileService.unlistenToJupyterConfig()
            setJupyterUrl(undefined);
            setjupyterJobId(undefined);
            setjupyterState(undefined);
            setJupyterInstanceInfo(undefined);
            setShowJupyterIframe(false);
        } else {
            message.error(res?.message ?? t('jupyter.cancel_failed'));
        }
    }

    const showCancelConfirm = () => {
        if (jupyterJobId === undefined) {
            return;
        }

        modal.confirm({
            title: t('jupyter.confirm_stop_title'),
            okText: t('jupyter.confirm_yes'),
            cancelText: t('jupyter.confirm_cancel'),
            onOk: cancelJobHandler,
        });
    };

    const ExtraButtonGroup = () => {
        return <>
            <Button size='large' onClick={fullScreenHandler} disabled={!jupyterUrl} shape="circle">
                <FullscreenOutlined />
            </Button>
            <Button size='large' onClick={showCancelConfirm} disabled={!jupyterJobId} shape="circle">
                <StopOutlined />
            </Button>
        </>
    }



    const InstanceReadyBlock = () => {
        const formatDate = (dateString: number) => {
            return dayjs(dateString * 1000).format('YYYY-MM-DD HH:mm:ss');
        };

        const formatExpiredDate = (start: number, limit: number) => {
            return dayjs(start * 1000).add(limit * 60 * 1000).format('YYYY-MM-DD HH:mm:ss');
        }

        return (
            <div className='flex flex-col w-full h-full justify-center items-center p-8'>
                <Title level={3} className='mb-6'>{t('jupyter.instance_ready')}</Title>

                <div className='bg-gray-50 dark:bg-[#1E1E1E] p-6 rounded-lg mb-6 w-full max-w-md'>
                    <div className='space-y-3'>
                        <div className='flex justify-between'>
                            <span className='font-medium'>{t('jupyter.start_time')}</span>
                            <span>{jupyterInstanceInfo?.timeStart ? formatDate(jupyterInstanceInfo.timeStart) : t('jupyter.unknown')}</span>
                        </div>
                        <div className='flex justify-between'>
                            <span className='font-medium'>{t('jupyter.expired_time')}</span>
                            <span>{jupyterInstanceInfo?.timeStart && jupyterInstanceInfo?.timeLimit ? formatExpiredDate(jupyterInstanceInfo.timeStart, jupyterInstanceInfo?.timeLimit) : t('jupyter.unknown')}</span>
                        </div>
                        <div className='flex justify-between'>
                            <span className='font-medium'>{t('jupyter.cpu_num')}</span>
                            <span>{jupyterInstanceInfo?.cpuCores || 0}</span>
                        </div>
                        <div className='flex justify-between'>
                            <span className='font-medium'>{t('jupyter.state')}</span>
                            {
                                jupyterInstanceInfo?.state === jobStateEnum.RUNNING && <Tag color='green'>{t('jupyter.state_ready')}</Tag>
                            }
                            {
                                jupyterInstanceInfo?.state !== jobStateEnum.RUNNING && <Tag color='yellow'>{t('jupyter.state_pending')}</Tag>
                            }
                        </div>
                        <div className='flex justify-between'>
                            <span className='font-medium'>{t('jupyter.node')}</span>
                            <span>{jupyterInstanceInfo?.node || t('jupyter.default_node')}</span>
                        </div>
                    </div>
                    {jupyterState === jobStateEnum.PENDING && <div className='mt-4 text-sm text-gray-500 dark:text-[#999999]'>
                        {t('jupyter.launch_hint')}
                    </div>}
                </div>

                <div className='space-y-4 w-full max-w-md'>
                    <div className='flex items-center justify-center'>
                        <Checkbox
                            checked={openInNewTab}
                            onChange={(e) => setOpenInNewTab(e.target.checked)}
                            className='mr-2'
                        >
                            {t('jupyter.open_new_tab')}
                        </Checkbox>
                    </div>

                    <div className='flex space-x-4 justify-center'>
                        <Button
                            size="large"
                            disabled={jupyterInstanceInfo?.state !== jobStateEnum.RUNNING}
                            onClick={handleOpenJupyter}
                            className='bg-blue-500 dark:bg-[#0E639C] hover:bg-blue-700 dark:hover:bg-[#1177BB] text-white dark:text-[#FFFFFF] font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline'
                        >
                            {t('jupyter.open')}
                        </Button>

                        <Button
                            size="large"
                            danger
                            onClick={showCancelConfirm}
                            className='font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline'
                        >
                            {t('jupyter.stop')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    };

    const RunningBlock = () => {
        return <div className='w-full h-full'>
            <iframe
                title='Jupyter'
                src={jupyterUrl}
                className='w-full h-full'
            />
            <ActionButtonGroup extraButton={ExtraButtonGroup()} />
        </div>
    }

    const InitializeBlock = () => {
        return <div className='relative flex flex-col w-full h-full justify-center items-center'>
            <Title level={3} className='mb-4'>{t('jupyter.no_instance')}</Title>
            <Paragraph>
                {t('jupyter.start_new')}
            </Paragraph>
            <Form className='w-80 mt-8' form={form} initialValues={{ cpu: 1, gpu: 0, partition: 'default' }}
                onFieldsChange={onFormChange} onFinish={onLaunchJupyter}>
                <Form.Item label={t('jupyter.partition')} name={'partition'}>
                    <Select placeholder={t('jupyter.partition_placeholder')} options={partitionOptions}>
                    </Select>
                </Form.Item>
                {form.getFieldValue('partition') !== "default" ? <>
                    <Form.Item
                        label={t('jupyter.cpu_count')}
                        name='cpu'
                        className='mb-4'
                        rules={[{ required: true, message: '' }]}
                    >
                        <InputNumber
                            disabled={form.getFieldValue('partition') === "default"}
                            min={1}
                            max={userResource?.data?.max_cpus || 1}
                            className='shadow appearance-none border rounded w-full text-gray-700 dark:text-[#CCCCCC] leading-tight focus:outline-none focus:shadow-outline'
                        />
                    </Form.Item>
                    <Form.Item
                        label={t('jupyter.gpu_count')}
                        name='gpu'
                        className='mb-4'
                        rules={[{ required: true, message: '' }]}
                    >
                        <InputNumber
                            disabled={form.getFieldValue('partition') === "default"}
                            min={0}
                            max={userResource?.data?.max_gpus || 0}
                            className='shadow appearance-none border rounded w-full text-gray-700 dark:text-[#CCCCCC] leading-tight focus:outline-none focus:shadow-outline'
                        />
                    </Form.Item>
                    <Form.Item label={t('jupyter.max_time')} name={'maxTime'} dependencies={['partition']}>
                        <InputNumber
                            disabled={form.getFieldValue('partition') === "default"}
                            min={1}
                            max={userResource?.data?.max_walltime && userResource?.data?.max_walltime !== "unlimited" ? parseWalltimeToMinutes(userResource?.data?.max_walltime) : Number.MAX_SAFE_INTEGER}
                            className='shadow appearance-none border rounded w-full text-gray-700 dark:text-[#CCCCCC] leading-tight focus:outline-none focus:shadow-outline'
                        />
                    </Form.Item>
                    <Form.Item label={t('jupyter.node_label')} name={'node'} dependencies={['partition']} >
                        <Select placeholder={t('jupyter.node_placeholder')} disabled={nodesOptions.length === 0} options={nodesOptions}>
                        </Select>
                    </Form.Item>
                </> : <></>}
                <Form.Item className='flex justify-center mt-8'>
                    <Button
                        size="large"
                        loading={isLoading}
                        type='primary'
                        htmlType='submit'
                        className='bg-blue-500 dark:bg-[#0E639C] hover:bg-blue-700 dark:hover:bg-[#1177BB] text-white dark:text-[#FFFFFF] font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline'
                    >
                        {t('jupyter.launch')}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    }

    return (
        <div className=' w-full h-full'>
            {
                jupyterState == jobStateEnum.RUNNING && showJupyterIframe ? <RunningBlock /> : <></>
            }
            {
                jupyterState == jobStateEnum.RUNNING || jupyterState == jobStateEnum.PENDING && !showJupyterIframe ? <InstanceReadyBlock /> : <></>
            }
            {/* {
                jupyterState == jobStateEnum.PENDING ? <PenddingBlock /> : <></>
            } */}
            {
                jupyterState == undefined ? <InitializeBlock /> : <></>
            }
        </div>
    );
};

export default JupyterPage;